/**
 * Lease Disclosure table: aggregates Assessment / Modification / Reassessment outputs
 * and trial balance FS sub-lines for lease liabilities.
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function parseMonthLabel(label: string): Date | null {
  const s = (label || "").trim();
  const m = s.match(/^([A-Za-z]{3})-(\d{2})$/);
  if (!m) return null;
  const monthIdx = MONTHS.findIndex((x) => x.toLowerCase() === m[1].toLowerCase());
  if (monthIdx < 0) return null;
  const yy = parseInt(m[2], 10);
  const year = 2000 + yy;
  return new Date(year, monthIdx, 1);
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

/** Indian FY ending 31 March: CY = Apr (Y-1) .. Mar Y when reporting end is Mar Y */
export function indianFyMarchBounds(rep: Date): { cyStart: Date; cyEnd: Date; pyStart: Date; pyEnd: Date } {
  const y = rep.getFullYear();
  const cyEnd = new Date(y, 2, 31);
  const cyStart = new Date(y - 1, 3, 1);
  const pyEnd = new Date(y - 1, 2, 31);
  const pyStart = new Date(y - 2, 3, 1);
  return { cyStart, cyEnd, pyStart, pyEnd };
}

export function monthLabelInRange(label: string, rangeStart: Date, rangeEnd: Date): boolean {
  const d = parseMonthLabel(label);
  if (!d) return false;
  return d >= startOfMonth(rangeStart) && d <= endOfMonth(rangeEnd);
}

function num(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return 0;
}

export function parseMmDdYyyy(s: string): Date | null {
  const t = (s || "").trim();
  const parts = t.split("/");
  if (parts.length !== 3) return null;
  const mm = parseInt(parts[0], 10);
  const dd = parseInt(parts[1], 10);
  const yyyy = parseInt(parts[2], 10);
  if (!Number.isFinite(mm) || !Number.isFinite(dd) || !Number.isFinite(yyyy)) return null;
  return new Date(yyyy, mm - 1, dd);
}

export function toTbDateKey(d: Date): string {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear() % 100).padStart(2, "0");
  return `${dd}/${mm}/${yy}`;
}

function getAmountForDate(amountDict: Record<string, unknown>, targetKey: string): number {
  if (amountDict && typeof amountDict === "object" && targetKey in amountDict) {
    return num(amountDict[targetKey]);
  }
  const keys = Object.keys(amountDict || {});
  if (!keys.length) return 0;
  let best = keys[0];
  let bestT = parseTbDateKey(best);
  const targetT = parseTbDateKey(targetKey);
  if (targetT && !Number.isNaN(targetT.getTime())) {
    for (const k of keys) {
      const kt = parseTbDateKey(k);
      if (!kt) continue;
      if (kt <= targetT && (!bestT || kt >= bestT)) {
        best = k;
        bestT = kt;
      }
    }
  }
  return num(amountDict[best]);
}

function getAmountForExactDate(amountDict: Record<string, unknown>, targetKey: string): number {
  if (!amountDict || typeof amountDict !== "object") return 0;
  if (!(targetKey in amountDict)) return 0;
  return num(amountDict[targetKey]);
}

function parseTbDateKey(k: string): Date | null {
  const parts = k.split("/");
  if (parts.length !== 3) return null;
  const d = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) - 1;
  let y = parseInt(parts[2], 10);
  if (y < 100) y += 2000;
  return new Date(y, m, d);
}

export function sumFsSubLinesAtDate(
  mappingData: unknown,
  fsSubLineIds: Set<number>,
  dateKey: string,
): number {
  if (!mappingData || typeof mappingData !== "object") return 0;
  let sum = 0;
  for (const ledgers of Object.values(mappingData as Record<string, unknown>)) {
    if (!Array.isArray(ledgers)) continue;
    for (const item of ledgers as Record<string, unknown>[]) {
      const sid = Number(
        item.fs_sub_line_id ??
          item.fsSubLineId ??
          item.note_line_id ??
          item.noteLineId ??
          -1,
      );
      if (!fsSubLineIds.has(sid)) continue;
      const amountDict = (item.amount || {}) as Record<string, unknown>;
      sum += getAmountForDate(amountDict, dateKey);
    }
  }
  return sum;
}

export function sumFsSubLinesAtExactDate(
  mappingData: unknown,
  fsSubLineIds: Set<number>,
  dateKey: string,
): number {
  if (!mappingData || typeof mappingData !== "object") return 0;
  let sum = 0;
  for (const ledgers of Object.values(mappingData as Record<string, unknown>)) {
    if (!Array.isArray(ledgers)) continue;
    for (const item of ledgers as Record<string, unknown>[]) {
      const sid = Number(
        item.fs_sub_line_id ??
          item.fsSubLineId ??
          item.note_line_id ??
          item.noteLineId ??
          -1,
      );
      if (!fsSubLineIds.has(sid)) continue;
      const amountDict = (item.amount || {}) as Record<string, unknown>;
      sum += getAmountForExactDate(amountDict, dateKey);
    }
  }
  return sum;
}

function prevMarchDateFromRepDate(repDate: Date): Date {
  const repYear = repDate.getFullYear();
  const repMonth1Based = repDate.getMonth() + 1;
  const repDay = repDate.getDate();
  const prevMarchYear =
    repMonth1Based > 3 || (repMonth1Based === 3 && repDay === 31) ? repYear - 1 : repYear - 2;
  return new Date(prevMarchYear, 2, 31);
}

function findLeaseByName(leases: unknown, name: string): Record<string, unknown> | null {
  if (!Array.isArray(leases)) return null;
  const n = String(name || "").trim();
  for (const L of leases) {
    if (!L || typeof L !== "object") continue;
    const li = (L as Record<string, unknown>).lease_info;
    if (li && typeof li === "object") {
      const nm = String((li as Record<string, unknown>)["Name of Lease"] ?? "").trim();
      if (nm === n) return L as Record<string, unknown>;
    }
  }
  return null;
}

function commencementDate(lease: Record<string, unknown> | null): Date | null {
  if (!lease) return null;
  const li = lease.lease_info;
  if (!li || typeof li !== "object") return null;
  const s = String((li as Record<string, unknown>)["Lease Commencement Date"] ?? "").trim();
  return parseMmDdYyyy(s);
}

function eventCommencementMonth(lease: Record<string, unknown> | null): Date | null {
  const d = commencementDate(lease);
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/**
 * Lease Disclosure / Maturity: iterate every lease in AssessmentOut only.
 * Modification and Reassessment data apply per lease when that lease exists in those files.
 */
function leaseNamesFromAssessmentOnly(assessmentLeases: unknown): string[] {
  const set = new Set<string>();
  if (!Array.isArray(assessmentLeases)) return [];
  for (const L of assessmentLeases) {
    if (!L || typeof L !== "object") continue;
    const li = (L as Record<string, unknown>).lease_info;
    if (li && typeof li === "object") {
      const nm = String((li as Record<string, unknown>)["Name of Lease"] ?? "").trim();
      if (nm) set.add(nm);
    }
  }
  return [...set].sort();
}

function prevMonthLabel(monthLabel: string): string | null {
  const d = parseMonthLabel(monthLabel);
  if (!d) return null;
  const p = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const m = MONTHS[p.getMonth()];
  const y = p.getFullYear() % 100;
  return `${m}-${String(y).padStart(2, "0")}`;
}

function earliestMonthFromMap(monthMap: Record<string, Record<string, unknown>>): Date | null {
  let best: Date | null = null;
  for (const label of Object.keys(monthMap)) {
    const d = parseMonthLabel(label);
    if (!d) continue;
    if (!best || d.getTime() < best.getTime()) best = d;
  }
  return best;
}

/** Modification wins if same month exists in both */
function pickRowForMonth(
  monthLabel: string,
  assessmentLease: Record<string, unknown> | null,
  modLease: Record<string, unknown> | null,
  reassLease: Record<string, unknown> | null,
): { row: Record<string, unknown> | null; source: "assessment" | "modification" | "reassessment" } {
  const modRow = findScheduleRow(modLease, monthLabel);
  const reassRow = findScheduleRow(reassLease, monthLabel);
  if (modRow && reassRow) return { row: modRow, source: "modification" };
  if (modRow) return { row: modRow, source: "modification" };
  if (reassRow) return { row: reassRow, source: "reassessment" };
  const aRow = findScheduleRow(assessmentLease, monthLabel);
  return { row: aRow, source: "assessment" };
}

function findScheduleRow(lease: Record<string, unknown> | null, monthLabel: string): Record<string, unknown> | null {
  if (!lease) return null;
  const sch = lease.schedule;
  if (!Array.isArray(sch)) return null;
  for (const r of sch) {
    if (!r || typeof r !== "object") continue;
    if (String((r as Record<string, unknown>)["Month"] ?? "").trim() === monthLabel) {
      return r as Record<string, unknown>;
    }
  }
  return null;
}

function parseScheduleStartDate(lease: Record<string, unknown> | null): Date | null {
  if (!lease) return null;
  const sch = lease.schedule;
  if (!Array.isArray(sch) || sch.length === 0) return null;
  const first = sch[0] as Record<string, unknown>;
  const monthLabel = String(first?.["Month"] ?? "").trim();
  if (!monthLabel) return null;
  return parseMonthLabel(monthLabel);
}

/** Latest start date among assessment, mod, reass for lease; modification wins if tied with reassessment */
function pickLatestLease(
  name: string,
  assessment: unknown,
  mod: unknown,
  reass: unknown,
): Record<string, unknown> | null {
  const a = findLeaseByName((assessment as Record<string, unknown>)?.leases, name);
  const m = findLeaseByName((mod as Record<string, unknown>)?.leases, name);
  const r = findLeaseByName((reass as Record<string, unknown>)?.leases, name);
  const at = a ? parseScheduleStartDate(a)?.getTime() ?? -Infinity : -Infinity;
  const mt = m ? parseScheduleStartDate(m)?.getTime() ?? -Infinity : -Infinity;
  const rt = r ? parseScheduleStartDate(r)?.getTime() ?? -Infinity : -Infinity;

  const maxT = Math.max(at, mt, rt);
  if (maxT === -Infinity) return null;

  // Tie-breaker: prefer modification > reassessment > assessment
  if (mt === maxT && m) return m;
  if (rt === maxT && r) return r;
  if (at === maxT && a) return a;
  return null;
}

export interface LeaseDisclosureComputed {
  reportingDateLabel: string;
  cyOpeningTb: number;
  pyOpeningTb: number;
  additionsCy: number;
  additionsPy: number;
  adjustmentsCy: number;
  adjustmentsPy: number;
  accretionCy: number;
  accretionPy: number;
  paymentsCy: number;
  paymentsPy: number;
  closingSumCy: number;
  closingSumPy: number;
  closingPerScheduleCy: number;
  closingPerSchedulePy: number;
  tbAtRepCy: number;
  tbAtRepPy: number;
  paymentTraceLines: string[];
}

export function computeLeaseDisclosure(
  reportingDate: Date,
  assessmentOut: unknown,
  modificationOut: unknown,
  reassessmentOut: unknown,
  trialBalanceMapping: unknown,
): LeaseDisclosureComputed {
  const { cyStart, cyEnd, pyStart, pyEnd } = indianFyMarchBounds(reportingDate);
  const repKey = toTbDateKey(reportingDate);

  const fsIds = new Set([20021, 20050]);
  // Opening balance uses the exact "target_date" derived from rep_date,
  // matching the Python snippet you provided (previous 31st March logic).
  const cyOpeningDate = prevMarchDateFromRepDate(reportingDate);
  const pyOpeningDate = prevMarchDateFromRepDate(
    new Date(reportingDate.getFullYear() - 1, reportingDate.getMonth(), reportingDate.getDate()),
  );
  const cyOpeningKey = toTbDateKey(cyOpeningDate);
  const pyOpeningKey = toTbDateKey(pyOpeningDate);

  const cyOpeningTb = sumFsSubLinesAtExactDate(trialBalanceMapping, fsIds, cyOpeningKey);
  const pyOpeningTb = sumFsSubLinesAtExactDate(trialBalanceMapping, fsIds, pyOpeningKey);

  // TB at reporting date for reconciliation rows.
  // Your Python snippet picks the value for the "previous 31st March" derived from rep_date,
  // and formats it as dd/mm/yy (e.g. 31/03/24). We replicate that here.
  const tbTargetCy = prevMarchDateFromRepDate(reportingDate);
  const tbAtRepCy = sumFsSubLinesAtExactDate(trialBalanceMapping, fsIds, toTbDateKey(tbTargetCy));

  const pyRepDate = new Date(reportingDate.getFullYear() - 1, reportingDate.getMonth(), reportingDate.getDate());
  const tbTargetPy = prevMarchDateFromRepDate(pyRepDate);
  const tbAtRepPy = sumFsSubLinesAtExactDate(trialBalanceMapping, fsIds, toTbDateKey(tbTargetPy));

  const assessLeases = (assessmentOut as Record<string, unknown>)?.leases;
  const names = leaseNamesFromAssessmentOnly(assessLeases);

  let additionsCy = 0;
  let additionsPy = 0;
  let adjustmentsCy = 0;
  let adjustmentsPy = 0;
  let accretionCy = 0;
  let accretionPy = 0;
  let paymentsCy = 0;
  let paymentsPy = 0;
  let closingPerScheduleCy = 0;
  let closingPerSchedulePy = 0;
  const paymentTraceLines: string[] = [];

  for (const name of names) {
    const aLease = findLeaseByName(assessLeases, name);
    const mLease = findLeaseByName((modificationOut as Record<string, unknown>)?.leases, name);
    const rLease = findLeaseByName((reassessmentOut as Record<string, unknown>)?.leases, name);
    const comm = commencementDate(aLease);
    if (comm) {
      const sch = Array.isArray(aLease?.schedule) ? (aLease!.schedule as Record<string, unknown>[]) : [];
      const firstOpening = sch.length > 0 ? num(sch[0]?.["Opening Balance"]) : 0;

      if (comm >= cyStart && comm <= cyEnd) {
        // Additions = sum of first opening balance (Sl No 1) for leases commencing in FY.
        additionsCy += firstOpening;
      }

      if (comm >= pyStart && comm <= pyEnd) {
        additionsPy += firstOpening;
      }
    }

    // Adjustment of Modifications/Reassessment:
    // Use the same signed delta formula as journal entry logic:
    // delta = OpeningBalance(event month = first schedule row) - ClosingBalance(prior month from assessment).
    // Add ALL available journal-style deltas (both modification and reassessment) for the lease.
    // Only current FY is computed here; previous year is loaded from saved recon (not from mod/reass JSON).
    const journalDeltaForEventLease = (
      eventLease: Record<string, unknown> | null,
    ): { eventMonth: string; delta: number } | null => {
      if (!eventLease) return null;
      const sch = eventLease.schedule;
      if (!Array.isArray(sch) || sch.length === 0) return null;
      const firstRow = sch[0] as Record<string, unknown>;
      const eventMonth = String(firstRow["Month"] ?? "").trim();
      if (!eventMonth) return null;
      const prev = prevMonthLabel(eventMonth);
      if (!prev) return null;
      const aPrev = findScheduleRow(aLease, prev);
      if (!aPrev) return null;
      const openingEvent = num(firstRow["Opening Balance"]);
      const closingPrior = num(aPrev["Closing Balance"]);
      return { eventMonth, delta: openingEvent - closingPrior };
    };

    const modJournalDelta = journalDeltaForEventLease(mLease);
    const reassJournalDelta = journalDeltaForEventLease(rLease);
    for (const j of [modJournalDelta, reassJournalDelta]) {
      if (!j) continue;
      if (monthLabelInRange(j.eventMonth, cyStart, cyEnd)) adjustmentsCy += j.delta;
    }

    // Accretion of Interest & Payments:
    // - Use assessment until the month before earliest event start (mod/reass).
    // - From event start onward, pick row by month using mod/reass precedence
    //   (modification wins when both exist for the same month), with safe fallback.
    // - Works whether only one event file exists, both exist, or neither exists.
    // - Payments are summed positive here; UI shows negative in UI.
    const aMap: Record<string, Record<string, unknown>> = {};
    const mMap: Record<string, Record<string, unknown>> = {};
    const rMap: Record<string, Record<string, unknown>> = {};

    if (Array.isArray(aLease?.schedule)) {
      for (const row of aLease.schedule as Record<string, unknown>[]) {
        if (!row || typeof row !== "object") continue;
        const ml = String(row["Month"] ?? "").trim();
        if (ml) aMap[ml] = row;
      }
    }
    if (Array.isArray(mLease?.schedule)) {
      for (const row of (mLease!.schedule as Record<string, unknown>[])) {
        if (!row || typeof row !== "object") continue;
        const ml = String(row["Month"] ?? "").trim();
        if (ml) mMap[ml] = row;
      }
    }
    if (Array.isArray(rLease?.schedule)) {
      for (const row of (rLease!.schedule as Record<string, unknown>[])) {
        if (!row || typeof row !== "object") continue;
        const ml = String(row["Month"] ?? "").trim();
        if (ml) rMap[ml] = row;
      }
    }

    // Switch point for blended stream is based on event-file lease commencement date.
    // If unavailable, fallback to earliest schedule month.
    const mStartDate = eventCommencementMonth(mLease) ?? earliestMonthFromMap(mMap);
    const rStartDate = eventCommencementMonth(rLease) ?? earliestMonthFromMap(rMap);
    let startDate: Date | null = null;
    let chosenMap: typeof aMap = aMap;
    if (mStartDate && rStartDate) {
      if (mStartDate.getTime() <= rStartDate.getTime()) {
        startDate = mStartDate;
        chosenMap = mMap; // tie -> modification
      } else {
        startDate = rStartDate;
        chosenMap = rMap;
      }
    } else if (mStartDate) {
      startDate = mStartDate;
      chosenMap = mMap;
    } else if (rStartDate) {
      startDate = rStartDate;
      chosenMap = rMap;
    }

    const repMonthDate = new Date(reportingDate.getFullYear(), reportingDate.getMonth(), 1);

    const monthLabels = new Set<string>([
      ...Object.keys(aMap),
      ...Object.keys(mMap),
      ...Object.keys(rMap),
    ]);

    const sorted = [...monthLabels].sort((x, y) => {
      const dx = parseMonthLabel(x);
      const dy = parseMonthLabel(y);
      return (dx?.getTime() ?? 0) - (dy?.getTime() ?? 0);
    });

    for (const ml of sorted) {
      const d = parseMonthLabel(ml);
      if (!d) continue;
      if (d.getTime() > repMonthDate.getTime()) continue;

      let row: Record<string, unknown> | undefined;
      let rowSource: "assessment" | "modification" | "reassessment" | "none" = "none";
      if (startDate && d.getTime() < startDate.getTime()) {
        row = aMap[ml];
        rowSource = row ? "assessment" : "none";
      } else if (startDate) {
        // After first event month, keep using only the chosen output stream.
        row = chosenMap[ml];
        if (row) {
          if (chosenMap === mMap) rowSource = "modification";
          else if (chosenMap === rMap) rowSource = "reassessment";
          else rowSource = "assessment";
        } else {
          rowSource = "none";
        }
      } else {
        row = aMap[ml];
        rowSource = row ? "assessment" : "none";
      }

      if (!row) continue;
      const intr = num(row["Interest"]);
      const rep = num(row["Repayment"] ?? row["Repaymentt"]);

      // TEMP DEBUG: trace which month/file contributes to Payments (shown in UI).
      paymentTraceLines.push(
        `[Payments] lease=${name} month=${ml} source=${rowSource} repayment=${Math.round(rep)}`,
      );

      if (monthLabelInRange(ml, cyStart, cyEnd)) {
        accretionCy += intr;
        paymentsCy += rep;
      }
      if (monthLabelInRange(ml, pyStart, pyEnd)) {
        accretionPy += intr;
        paymentsPy += rep;
      }
    }

    const repMonthLabel = (() => {
      const m = reportingDate.getMonth();
      const label = `${MONTHS[m]}-${String(reportingDate.getFullYear() % 100).padStart(2, "0")}`;
      return label;
    })();
    const pyRepDate = new Date(reportingDate.getFullYear() - 1, reportingDate.getMonth(), reportingDate.getDate());
    const pyRepMonthLabel = `${MONTHS[pyRepDate.getMonth()]}-${String(pyRepDate.getFullYear() % 100).padStart(2, "0")}`;

    const best = pickLatestLease(name, assessmentOut, modificationOut, reassessmentOut);
    const rowCy = findScheduleRow(best, repMonthLabel) ?? findScheduleRow(aLease, repMonthLabel);
    const rowPy = findScheduleRow(best, pyRepMonthLabel) ?? findScheduleRow(aLease, pyRepMonthLabel);
    closingPerScheduleCy += num(rowCy?.["Closing Balance"]);
    closingPerSchedulePy += num(rowPy?.["Closing Balance"]);
  }

  const closingSumCy =
    cyOpeningTb + additionsCy + adjustmentsCy + accretionCy - paymentsCy;
  const closingSumPy =
    pyOpeningTb + additionsPy + adjustmentsPy + accretionPy - paymentsPy;

  return {
    reportingDateLabel: repKey,
    cyOpeningTb,
    pyOpeningTb,
    additionsCy,
    additionsPy,
    adjustmentsCy,
    adjustmentsPy,
    accretionCy,
    accretionPy,
    paymentsCy,
    paymentsPy,
    closingSumCy,
    closingSumPy,
    closingPerScheduleCy,
    closingPerSchedulePy,
    tbAtRepCy,
    tbAtRepPy,
    paymentTraceLines,
  };
}

export interface LeaseMaturityAnalysisComputed {
  currentYear: {
    year1: number;
    year2: number;
    year3: number;
    year4: number;
    year5: number;
    year6Onwards: number;
    unmaturedFinanceCharges: number; // negative
    closingBalance: number;
    nonCurrent: number;
    current: number;
  };
  previousYear: {
    year1: number;
    year2: number;
    year3: number;
    year4: number;
    year5: number;
    year6Onwards: number;
    unmaturedFinanceCharges: number; // negative
    closingBalance: number;
    nonCurrent: number;
    current: number;
  };
}

function monthDiff(base: Date, target: Date): number {
  // base/target expected to be in the same day/month-start sense; use year/month only.
  return (target.getFullYear() - base.getFullYear()) * 12 + (target.getMonth() - base.getMonth());
}

function formatLeaseNameFromException(lease: any): string | null {
  const li = lease?.lease_info;
  if (!li || typeof li !== "object") return null;
  const nm = String(li["Name of Lease"] ?? "").trim();
  return nm || null;
}

function buildExceptionLeaseNameSet(exceptionOut: unknown): Set<string> {
  const s = new Set<string>();
  const arr = (exceptionOut as Record<string, any>)?.leases;
  if (!Array.isArray(arr)) return s;
  for (const l of arr) {
    const nm = formatLeaseNameFromException(l);
    if (nm) s.add(nm);
  }
  return s;
}

function computeForBaseDate(
  baseDate: Date,
  assessmentOut: unknown,
  modificationOut: unknown,
  reassessmentOut: unknown,
  exceptionLeaseNames: Set<string>,
): LeaseMaturityAnalysisComputed["currentYear"] {
  const baseMonth = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);

  let year1 = 0;
  let year2 = 0;
  let year3 = 0;
  let year4 = 0;
  let year5 = 0;
  let year6Onwards = 0;
  let interestAfter = 0;
  let interestYear1 = 0;

  const names = leaseNamesFromAssessmentOnly((assessmentOut as Record<string, unknown>)?.leases).filter(
    (n) => !exceptionLeaseNames.has(n),
  );

  for (const name of names) {
    const picked = pickLatestLease(name, assessmentOut, modificationOut, reassessmentOut);
    if (!picked) continue;
    if (!Array.isArray((picked as Record<string, unknown>).schedule)) continue;

    const sch = (picked as Record<string, unknown>).schedule as Record<string, unknown>[];
    for (const row of sch) {
      if (!row || typeof row !== "object") continue;
      const ml = String((row as Record<string, unknown>)["Month"] ?? "").trim();
      const d = parseMonthLabel(ml);
      if (!d) continue;

      const md = monthDiff(baseMonth, d);
      // md=0 means base month; only consider strictly AFTER base date for interestAfter and all maturities
      if (md <= 0) continue;

      const repayment = num((row as Record<string, unknown>)["Repayment"] ?? (row as Record<string, unknown>)["Repaymentt"]);
      const interest = num((row as Record<string, unknown>)["Interest"]);

      if (md >= 1 && md <= 12) {
        year1 += repayment;
        interestYear1 += interest;
      } else if (md >= 13 && md <= 24) {
        year2 += repayment;
      } else if (md >= 25 && md <= 36) {
        year3 += repayment;
      } else if (md >= 37 && md <= 48) {
        year4 += repayment;
      } else if (md >= 49 && md <= 60) {
        year5 += repayment;
      } else if (md >= 61) {
        year6Onwards += repayment;
      }

      interestAfter += interest;
    }
  }

  const unmaturedFinanceCharges = -interestAfter; // negative
  const current = year1 - interestYear1;
  // Round buckets to whole numbers so the UI "Closing Balance" matches the sum of the displayed rows.
  const year1R = Math.round(year1);
  const year2R = Math.round(year2);
  const year3R = Math.round(year3);
  const year4R = Math.round(year4);
  const year5R = Math.round(year5);
  const year6OnwardsR = Math.round(year6Onwards);
  const unmaturedR = Math.round(unmaturedFinanceCharges);

  const closingBalanceFromBuckets = year1R + year2R + year3R + year4R + year5R + year6OnwardsR + unmaturedR;
  const currentR = Math.round(current);
  const nonCurrent = closingBalanceFromBuckets - currentR;

  return {
    year1: year1R,
    year2: year2R,
    year3: year3R,
    year4: year4R,
    year5: year5R,
    year6Onwards: year6OnwardsR,
    unmaturedFinanceCharges: unmaturedR,
    closingBalance: closingBalanceFromBuckets,
    nonCurrent,
    current: currentR,
  };
}

export function computeLeaseMaturityAnalysis(
  reportingDate: Date,
  assessmentOut: unknown,
  modificationOut: unknown,
  reassessmentOut: unknown,
  leaseAccountingExceptionOut: unknown,
): LeaseMaturityAnalysisComputed {
  // Requirement: Maturity Analysis should be calculated for *all* leases in AssessmentOut.
  // We intentionally do NOT exclude Lease Accounting Exception leases.
  void leaseAccountingExceptionOut;
  const exceptionLeaseNames = new Set<string>();

  const currentYear = computeForBaseDate(
    reportingDate,
    assessmentOut,
    modificationOut,
    reassessmentOut,
    exceptionLeaseNames,
  );

  const prevBase = new Date(reportingDate.getFullYear() - 1, reportingDate.getMonth(), reportingDate.getDate());
  const previousYear = computeForBaseDate(
    prevBase,
    assessmentOut,
    modificationOut,
    reassessmentOut,
    exceptionLeaseNames,
  );

  return { currentYear, previousYear };
}

export interface LeaseAccountingExceptionDisclosureComputed {
  currentYear: {
    shortTermExpense: number;
    lowValueExpense: number;
  };
  previousYear: {
    shortTermExpense: number;
    lowValueExpense: number;
  };
}

function normalizeLeaseType(v: unknown): string {
  return String(v ?? "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
}

function isShortTermLeaseType(v: unknown): boolean {
  const t = normalizeLeaseType(v);
  return t === "shorttermlease";
}

function isLowValueLeaseType(v: unknown): boolean {
  const t = normalizeLeaseType(v);
  return t === "lowvaluelease";
}

function buildAccExpTypeMap(accExpInp: unknown): Map<string, string> {
  const m = new Map<string, string>();
  const leases = (accExpInp as Record<string, unknown>)?.leases;
  if (!Array.isArray(leases)) return m;
  for (const L of leases) {
    if (!L || typeof L !== "object") continue;
    const obj = L as Record<string, unknown>;
    const name = String(obj.lease_name ?? "").trim();
    const type = String(obj.lease_type ?? "").trim();
    if (name && type) m.set(name, type);
  }
  return m;
}

function accExpLeaseType(
  leaseObj: Record<string, unknown>,
  typeMap: Map<string, string>,
): string {
  const li = leaseObj.lease_info;
  const leaseInfo = li && typeof li === "object" ? (li as Record<string, unknown>) : null;
  const name = String(leaseInfo?.["Name of Lease"] ?? "").trim();

  const fromOut =
    String(leaseInfo?.["Lease Type"] ?? leaseInfo?.lease_type ?? leaseObj.lease_type ?? "").trim();
  if (fromOut) return fromOut;
  if (name && typeMap.has(name)) return String(typeMap.get(name));
  return "";
}

function sumAccExpLeaseTypeInRange(
  accExpOut: unknown,
  accExpInp: unknown,
  rangeStart: Date,
  rangeEnd: Date,
  kind: "shortTerm" | "lowValue",
): number {
  const leases = (accExpOut as Record<string, unknown>)?.leases;
  if (!Array.isArray(leases)) return 0;
  const typeMap = buildAccExpTypeMap(accExpInp);

  let total = 0;
  for (const lease of leases) {
    if (!lease || typeof lease !== "object") continue;
    const L = lease as Record<string, unknown>;
    const lt = accExpLeaseType(L, typeMap);
    const typeMatches =
      kind === "shortTerm" ? isShortTermLeaseType(lt) : isLowValueLeaseType(lt);
    if (!typeMatches) continue;

    const sch = L.schedule;
    if (!Array.isArray(sch)) continue;
    for (const row of sch) {
      if (!row || typeof row !== "object") continue;
      const R = row as Record<string, unknown>;
      const monthLabel = String(R["Month"] ?? "").trim();
      if (!monthLabelInRange(monthLabel, rangeStart, rangeEnd)) continue;
      total += num(R["Straight-lining expense"]);
    }
  }
  return Math.round(total);
}

export function computeLeaseAccountingExceptionDisclosure(
  reportingDate: Date,
  accExpOut: unknown,
  accExpInp?: unknown,
): LeaseAccountingExceptionDisclosureComputed {
  const fyStartYear = reportingDate.getMonth() >= 3 ? reportingDate.getFullYear() : reportingDate.getFullYear() - 1;
  const cyStart = new Date(fyStartYear, 3, 1); // 1-Apr of FY containing reportingDate
  const pyStart = new Date(fyStartYear - 1, 3, 1);
  const repMonthEnd = endOfMonth(reportingDate);
  const prevRep = new Date(reportingDate.getFullYear() - 1, reportingDate.getMonth(), reportingDate.getDate());
  const prevRepMonthEnd = endOfMonth(prevRep);

  const cyShort = sumAccExpLeaseTypeInRange(accExpOut, accExpInp, cyStart, repMonthEnd, "shortTerm");
  const cyLow = sumAccExpLeaseTypeInRange(accExpOut, accExpInp, cyStart, repMonthEnd, "lowValue");
  const pyShort = sumAccExpLeaseTypeInRange(accExpOut, accExpInp, pyStart, prevRepMonthEnd, "shortTerm");
  const pyLow = sumAccExpLeaseTypeInRange(accExpOut, accExpInp, pyStart, prevRepMonthEnd, "lowValue");

  return {
    currentYear: { shortTermExpense: cyShort, lowValueExpense: cyLow },
    previousYear: { shortTermExpense: pyShort, lowValueExpense: pyLow },
  };
}
